import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Loader2, ShieldCheck, Copy, Check, LogOut } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import QRCode from 'qrcode';

interface TwoFactorSetupProps {
  isRequired?: boolean;
  onComplete: () => void;
  onSkip?: () => void;
  /** Called when the user wants to sign out instead of completing setup */
  onSignOut?: () => void;
  /** Called when setup discovers the user already has a verified factor — should switch to verify screen */
  onAlreadyEnrolled?: (factorId: string) => void;
}

export function TwoFactorSetup({ isRequired = false, onComplete, onSkip, onSignOut, onAlreadyEnrolled }: TwoFactorSetupProps) {
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [secret, setSecret] = useState<string>('');
  const [factorId, setFactorId] = useState<string>('');
  const [verifyCode, setVerifyCode] = useState('');
  const [isEnrolling, setIsEnrolling] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    enrollFactor();
  }, []);

  const enrollFactor = async () => {
    setIsEnrolling(true);
    try {
      // First check: does the user already have a verified TOTP factor?
      // This prevents re-enrollment when the guard incorrectly routes here.
      const { data: existingFactors } = await supabase.auth.mfa.listFactors();
      const alreadyVerified = existingFactors?.totp?.filter(f => f.status === 'verified') ?? [];

      // Also check via user.factors as a fallback
      if (alreadyVerified.length === 0) {
        const { data: { user } } = await supabase.auth.getUser();
        const userVerified = (user as any)?.factors?.filter(
          (f: any) => f.factor_type === 'totp' && f.status === 'verified'
        ) ?? [];
        if (userVerified.length > 0) {
          alreadyVerified.push(...userVerified);
        }
      }

      if (alreadyVerified.length > 0) {
        // User already has 2FA — redirect to verify screen or just complete
        if (onAlreadyEnrolled) {
          onAlreadyEnrolled(alreadyVerified[0].id);
        } else {
          // No verify handler — trigger a session challenge + verify flow
          // by reloading so the guard picks up the correct state
          window.location.reload();
        }
        return;
      }

      // Clean up any existing unverified TOTP factors to avoid name conflicts
      const factorsToCleanup =
        existingFactors?.all?.filter(
          (factor) => factor.factor_type === 'totp' && (factor.status as string) !== 'verified'
        ) ?? [];

      for (const factor of factorsToCleanup) {
        await supabase.auth.mfa.unenroll({ factorId: factor.id });
      }

      let { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'Authenticator App',
      });

      if (error?.code === 'mfa_factor_name_conflict') {
        await cleanupDanglingTotpFactors();
        const retryResult = await supabase.auth.mfa.enroll({
          factorType: 'totp',
          friendlyName: `Authenticator App ${Date.now()}`,
        });
        data = retryResult.data;
        error = retryResult.error;
      }

      if (error) throw error;

      setFactorId(data.id);
      // Generate QR code image from the TOTP URI
      const qrDataUrl = await QRCode.toDataURL(data.totp.uri, {
        width: 200,
        margin: 2,
        color: { dark: '#000000', light: '#ffffff' },
      });
      setQrCodeUrl(qrDataUrl);
      setSecret(data.totp.secret);

    } catch (error: any) {
      toast({
        title: 'Error setting up 2FA',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsEnrolling(false);
    }
  };

  const handleVerify = async () => {
    if (verifyCode.length !== 6) return;

    setIsVerifying(true);
    try {
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId,
      });

      if (challengeError) throw challengeError;

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code: verifyCode,
      });

      if (verifyError) throw verifyError;

      toast({
        title: 'Two-factor authentication enabled',
        description: 'Your account is now protected with 2FA.',
      });
      onComplete();
    } catch (error: any) {
      toast({
        title: 'Verification failed',
        description: 'Invalid code. Please try again.',
        variant: 'destructive',
      });
      setVerifyCode('');
    } finally {
      setIsVerifying(false);
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Card className="w-full max-w-md mx-auto shadow-2xl border-border/50">
      <CardHeader className="text-center space-y-2">
        <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
          <ShieldCheck className="h-6 w-6 text-primary" />
        </div>
        <CardTitle className="text-xl">
          {isRequired ? 'Set Up Two-Factor Authentication' : 'Enable Two-Factor Authentication'}
        </CardTitle>
        <CardDescription>
          {isRequired
            ? 'As an admin, two-factor authentication is required for your account security.'
            : 'Add an extra layer of security to your account.'}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {isEnrolling ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground text-center">
                Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
              </p>

              <div className="flex justify-center">
                {qrCodeUrl && (
                  <div className="p-3 bg-white rounded-lg border">
                    <img src={qrCodeUrl} alt="2FA QR Code" className="w-[200px] h-[200px]" />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <p className="text-xs text-muted-foreground text-center">
                  Or enter this secret manually:
                </p>
                <div className="flex items-center justify-center gap-2">
                  <code className="text-xs bg-muted px-3 py-1.5 rounded font-mono select-all break-all max-w-[240px]">
                    {secret}
                  </code>
                  <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={copySecret}>
                    {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  </Button>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <p className="text-sm font-medium text-center">Enter the 6-digit code from your app</p>
              <div className="flex justify-center">
                <InputOTP
                  maxLength={6}
                  value={verifyCode}
                  onChange={(value) => {
                    setVerifyCode(value);
                  }}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>

              <Button
                className="w-full"
                onClick={handleVerify}
                disabled={verifyCode.length !== 6 || isVerifying}
              >
                {isVerifying ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify & Enable 2FA'
                )}
              </Button>

              {!isRequired && onSkip && (
                <Button variant="ghost" className="w-full text-muted-foreground" onClick={onSkip}>
                  Skip for now
                </Button>
              )}

              {onSignOut && (
                <Button variant="ghost" className="w-full text-muted-foreground" onClick={onSignOut}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </Button>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
